import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTranslation } from 'react-i18next';
import PageScaffold from '../components/PageScaffold';
import { authFetch } from '../utils/auth';
import { downloadBlob } from '../utils/caregiverApi';

interface GeneratedDocument {
  id: number;
  userId: number;
  templateId: number | null;
  documentName: string;
  formName: string | null;
  createdAt: string;
  template: { id: number; templateName: string | null } | null;
  user: { id: number; name: string } | null;
}

interface UserGroup {
  userId: number;
  userName: string;
  documents: GeneratedDocument[];
  lastGenerated: string;
}

type DocSortColumn = 'documentName' | 'formName' | 'createdAt';
type SortDirection = 'asc' | 'desc';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || 'Request failed');
  }
  return data as T;
}

function DocumentViewerDialog({
  document,
  onClose,
}: {
  document: GeneratedDocument | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cleanup = useCallback(() => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setError(null);
  }, [pdfUrl]);

  useEffect(() => {
    if (!document) return undefined;
    const selectedDocument = document;
    let cancelled = false;

    async function loadDocument() {
      setLoading(true);
      setError(null);
      try {
        const response = await authFetch(`/api/documents/${selectedDocument.id}`);
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.message || 'Failed to load document');
        }
        const nextUrl = URL.createObjectURL(await response.blob());
        if (cancelled) {
          URL.revokeObjectURL(nextUrl);
          return;
        }
        setPdfUrl(nextUrl);
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError instanceof Error ? loadError.message : 'Failed to load document');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void loadDocument();
    return () => {
      cancelled = true;
    };
  }, [document]);

  const handleClose = () => {
    cleanup();
    onClose();
  };

  const handleDownload = () => {
    if (!pdfUrl || !document) return;
    const link = window.document.createElement('a');
    link.href = pdfUrl;
    link.download = `${document.documentName || `document-${document.id}`}.pdf`;
    window.document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <Dialog open={Boolean(document)} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          bgcolor: '#330662',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box>
          <Typography component="span" variant="h6">
            {document?.documentName || t('Document', 'Document')}
          </Typography>
          {document && (
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              {document.user?.name || t('Unknown', 'Unknown')} · {document.template?.templateName || t('N/A', 'N/A')} · {formatDate(document.createdAt)}
            </Typography>
          )}
        </Box>
        {pdfUrl && (
          <Button variant="contained" color="inherit" onClick={handleDownload} sx={{ color: '#330662' }}>
            {t('documents.downloadPdf', 'Download PDF')}
          </Button>
        )}
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: '#f5f5f5', height: '80vh' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ py: 4, textAlign: 'center' }}>
            {error}
          </Typography>
        ) : pdfUrl ? (
          <Box
            component="iframe"
            src={pdfUrl}
            title={document?.documentName || t('documents.documentPdf', 'Document PDF')}
            sx={{ width: '100%', height: '100%', border: 0, bgcolor: 'white', borderRadius: 1 }}
          />
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('Close', 'Close')}</Button>
      </DialogActions>
    </Dialog>
  );
}

function UserRow({
  group,
  onView,
  onDownload,
  onDelete,
}: {
  group: UserGroup;
  onView: (document: GeneratedDocument) => void;
  onDownload: (document: GeneratedDocument) => void;
  onDelete: (id: number) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [sortColumn, setSortColumn] = useState<DocSortColumn>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = (column: DocSortColumn) => {
    if (sortColumn === column) {
      setSortDirection((direction) => (direction === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortColumn(column);
    setSortDirection('asc');
  };

  const sortedDocuments = [...group.documents].sort((a, b) => {
    const getValue = (document: GeneratedDocument): string | number => {
      if (sortColumn === 'createdAt') return new Date(document.createdAt).getTime();
      return String(document[sortColumn] || '').toLowerCase();
    };
    const aValue = getValue(a);
    const bValue = getValue(b);
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <>
      <TableRow
        hover
        onClick={() => setOpen(!open)}
        sx={{ cursor: 'pointer', '& > *': { borderBottom: open ? 'none' : undefined } }}
      >
        <TableCell sx={{ width: 40, pr: 0 }}>
          <IconButton size="small">
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon fontSize="small" color="action" />
            <Typography fontWeight={600}>{group.userName}</Typography>
          </Box>
        </TableCell>
        <TableCell align="center">
          <Typography variant="body2">{group.documents.length}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{formatDate(group.lastGenerated)}</Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={4} sx={{ py: 0, px: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ mx: 2, my: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortColumn === 'documentName'}
                        direction={sortColumn === 'documentName' ? sortDirection : 'asc'}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleSort('documentName');
                        }}
                      >
                        {t('documents.documentName', 'Document Name')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{t('documents.sourceTemplate', 'Source Template')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortColumn === 'formName'}
                        direction={sortColumn === 'formName' ? sortDirection : 'asc'}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleSort('formName');
                        }}
                      >
                        {t('templates.formName', 'Form Name')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortColumn === 'createdAt'}
                        direction={sortColumn === 'createdAt' ? sortDirection : 'asc'}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleSort('createdAt');
                        }}
                      >
                        {t('documents.generatedDate', 'Generated Date')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{t('common.actions', 'Actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedDocuments.map((document) => (
                    <TableRow key={document.id} hover>
                      <TableCell>{document.documentName || t('N/A', 'N/A')}</TableCell>
                      <TableCell>{document.template?.templateName || t('N/A', 'N/A')}</TableCell>
                      <TableCell>{document.formName || t('N/A', 'N/A')}</TableCell>
                      <TableCell>{formatDate(document.createdAt)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title={t('View', 'View')}>
                            <IconButton
                              size="small"
                              color="info"
                              onClick={(event) => {
                                event.stopPropagation();
                                onView(document);
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('Download', 'Download')}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(event) => {
                                event.stopPropagation();
                                onDownload(document);
                              }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('Delete', 'Delete')}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(event) => {
                                event.stopPropagation();
                                onDelete(document.id);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function Documents() {
  const { t } = useTranslation();
  const [documents, setDocuments] = useState<GeneratedDocument[]>([]);
  const [viewingDoc, setViewingDoc] = useState<GeneratedDocument | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [nameSearch, setNameSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(page + 1),
        limit: String(rowsPerPage),
      });
      if (nameSearch) params.set('search', nameSearch);

      const response = await authFetch(`/api/documents?${params}`);
      const data = await parseJsonResponse<{
        documents: GeneratedDocument[];
        pagination: { totalUsers: number };
      }>(response);

      setDocuments(data.documents);
      setTotalUsers(data.pagination.totalUsers);
    } catch (fetchError) {
      setError(fetchError instanceof Error ? fetchError.message : t('documents.loadFailed', 'Failed to load documents'));
    } finally {
      setLoading(false);
    }
  }, [nameSearch, page, rowsPerPage, t]);

  useEffect(() => {
    void fetchDocuments();
  }, [fetchDocuments]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setNameSearch(searchInput);
      setPage(0);
    }, 300);

    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('documents.deleteConfirm', 'Are you sure you want to delete this document?'))) return;

    setError(null);
    try {
      const response = await authFetch(`/api/documents/${id}`, { method: 'DELETE' });
      await parseJsonResponse(response);
      await fetchDocuments();
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : t('documents.deleteFailed', 'Failed to delete document'));
    }
  };

  const handleDownload = async (document: GeneratedDocument) => {
    setError(null);
    try {
      const response = await authFetch(`/api/documents/${document.id}`);
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || t('documents.downloadFailed', 'Failed to download document'));
      }
      downloadBlob(await response.blob(), `${document.documentName || `document-${document.id}`}.pdf`);
    } catch (downloadError) {
      setError(downloadError instanceof Error ? downloadError.message : t('documents.downloadFailed', 'Failed to download document'));
    }
  };

  const userGroups: UserGroup[] = useMemo(() => {
    const map = new Map<number, UserGroup>();
    for (const document of documents) {
      if (!map.has(document.userId)) {
        map.set(document.userId, {
          userId: document.userId,
          userName: document.user?.name || t('Unknown', 'Unknown'),
          documents: [],
          lastGenerated: document.createdAt,
        });
      }

      const group = map.get(document.userId)!;
      group.documents.push(document);
      if (new Date(document.createdAt) > new Date(group.lastGenerated)) {
        group.lastGenerated = document.createdAt;
      }
    }

    return Array.from(map.values()).sort((a, b) => (
      a.userName.toLowerCase().localeCompare(b.userName.toLowerCase())
    ));
  }, [documents, t]);

  return (
    <PageScaffold title={t('documents.generatedDocuments', 'Generated Documents')}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TextField
          fullWidth
          size="small"
          placeholder={t('documents.searchByName', 'Search by name...')}
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
          sx={{ mb: 2, maxWidth: 400 }}
        />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : documents.length === 0 ? (
          <Typography sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
            {nameSearch ? t('documents.noUsersMatch', 'No users match your search') : t('documents.noneGenerated', 'No generated documents yet')}
          </Typography>
        ) : (
          <>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 620 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40 }} />
                    <TableCell sx={{ fontWeight: 600 }}>{t('User', 'User')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">{t('Documents', 'Documents')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{t('documents.lastGenerated', 'Last Generated')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {userGroups.map((group) => (
                    <UserRow
                      key={group.userId}
                      group={group}
                      onView={setViewingDoc}
                      onDownload={(document) => void handleDownload(document)}
                      onDelete={(id) => void handleDelete(id)}
                    />
                  ))}
                </TableBody>
              </Table>
            </Box>
            <TablePagination
              component="div"
              count={totalUsers}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(Number.parseInt(event.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}

      <DocumentViewerDialog document={viewingDoc} onClose={() => setViewingDoc(null)} />
    </PageScaffold>
  );
}
